import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, ButtonBase, Stack, Typography } from '@mui/material';
import { LivyMessage } from './LivyMessage';
import { useCapabilities } from './useCapabilities';
import { LivyComposer } from './LivyComposer';
import { COLUMN, SCROLLER, SUGGESTION_CHIP } from './styles';
import { LivyTheme } from './LivyTheme';
import { isReplayConversation } from './history';
import { LivyLiveBanner, LivyLiveContext } from './LivyLive';
const FADE_IN = {
  '@keyframes livyFadeIn': {
    from: {
      opacity: 0,
      transform: 'translateY(8px)'
    },
    to: {
      opacity: 1,
      transform: 'none'
    }
  },
  animation: 'livyFadeIn .5s cubic-bezier(.2,.8,.2,1) both',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none'
  }
};
const SUBTITLE = {
  account: 'I can read your properties, residents, bills and billing cycles, and pull reports for you.',
  knowledge: "I answer questions about Livable from our help articles. I can't see any account."
};
export const DISCLAIMER = {
  account: 'Livy reads live account data and can be wrong. Double-check anything you act on.',
  knowledge: "Livy answers from Livable's knowledge base and can be wrong. It cannot see any account."
};
const ICON_PATHS = [[/rate|recover|report|trend/i, 'M3 3v18h18M7 15l4-4 3 3 5-6'], [/block|pending|closing|stuck/i, 'M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01'], [/utilit|bill|jump/i, 'M13 2 4 14h7l-1 8 9-12h-7l1-8z'], [/propert|unit|resident|portfolio/i, 'M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6']];

/** A glyph that hints at what the starter asks about; the label carries the meaning. */
function StarterIcon(_ref) {
  var _ICON_PATHS$find$, _ICON_PATHS$find;
  let text = _ref.text;
  const d = (_ICON_PATHS$find$ = (_ICON_PATHS$find = ICON_PATHS.find(_ref2 => {
    let _ref3 = _slicedToArray(_ref2, 1),
      re = _ref3[0];
    return re.test(text);
  })) === null || _ICON_PATHS$find === void 0 ? void 0 : _ICON_PATHS$find[1]) !== null && _ICON_PATHS$find$ !== void 0 ? _ICON_PATHS$find$ : 'M12 3l2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5z';
  return /*#__PURE__*/React.createElement("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement("path", {
    d: d
  }));
}
function LivyPanelBody(_ref4) {
  let messages = _ref4.messages,
    streaming = _ref4.streaming,
    error = _ref4.error,
    companyLabel = _ref4.companyLabel,
    _ref4$showScope = _ref4.showScope,
    showScope = _ref4$showScope === void 0 ? true : _ref4$showScope,
    conversationId = _ref4.conversationId,
    surface = _ref4.surface,
    _ref4$starters = _ref4.starters,
    starters = _ref4$starters === void 0 ? [] : _ref4$starters,
    _ref4$reach = _ref4.reach,
    reach = _ref4$reach === void 0 ? 'account' : _ref4$reach,
    subtitle = _ref4.subtitle,
    reports = _ref4.reports,
    pageHelp = _ref4.pageHelp,
    onPageHelp = _ref4.onPageHelp,
    onSend = _ref4.onSend,
    readOnlyProp = _ref4.readOnly,
    onStop = _ref4.onStop,
    onRetry = _ref4.onRetry,
    _ref4$active = _ref4.active,
    active = _ref4$active === void 0 ? true : _ref4$active,
    _ref4$live = _ref4.live,
    live = _ref4$live === void 0 ? null : _ref4$live;
  const readOnly = readOnlyProp || isReplayConversation(conversationId);
  const endRef = useRef(null);
  const scrollerRef = useRef(null);
  const _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    atBottom = _useState2[0],
    setAtBottom = _useState2[1];
  // Staff only: the tool catalog is not filtered by customer type and reads as a developer list.
  const capabilities = useCapabilities(surface !== null && surface !== void 0 ? surface : 'admin', !reports);
  useEffect(() => {
    var _endRef$current;
    // Only follow the stream when the reader is already at the bottom. Yanking the view down while
    // someone is reading an earlier answer is the rudest thing a chat can do.
    if (atBottom) (_endRef$current = endRef.current) === null || _endRef$current === void 0 || _endRef$current.scrollIntoView({
      block: 'end',
      behavior: 'smooth'
    });
  }, [messages, atBottom]);
  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
  };
  const empty = messages.length === 0;
  // Help, reports and the full tool catalog sit behind the composer's +, so the opening screen is
  // the heading, the box and a few suggestions.
  const actions = [...(pageHelp && onPageHelp ? [{
    label: 'Help for this page',
    hint: pageHelp,
    onClick: onPageHelp
  }] : []), ...(reports !== null && reports !== void 0 ? reports : []).map(r => ({
    label: r,
    group: 'Popular reports',
    onClick: () => onSend(r)
  })), ...capabilities.flatMap(g => g.asks.map(a => ({
    label: a,
    group: g.label,
    onClick: () => onSend(a)
  })))];
  return /*#__PURE__*/React.createElement(Stack, {
    sx: {
      height: '100%',
      minHeight: 0
    }
  }, showScope && companyLabel && reach === 'account' && /*#__PURE__*/React.createElement(Box, {
    sx: {
      px: {
        xs: '16px',
        sm: '24px'
      },
      py: '7px',
      borderBottom: 1,
      borderColor: 'divider'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: _objectSpread(_objectSpread({}, COLUMN), {}, {
      height: empty ? '100%' : undefined
    })
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11.5,
      color: 'text.secondary',
      letterSpacing: '.04em'
    }
  }, "Scope", /*#__PURE__*/React.createElement(Box, {
    component: "span",
    sx: {
      color: 'text.primary',
      fontWeight: 600,
      ml: '7px'
    }
  }, companyLabel)))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      position: 'relative',
      flex: '1 1 0',
      minHeight: 0,
      display: 'flex'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: SCROLLER,
    ref: scrollerRef,
    onScroll: handleScroll
  }, /*#__PURE__*/React.createElement(Box, {
    sx: _objectSpread(_objectSpread({}, COLUMN), {}, {
      minHeight: empty ? '100%' : undefined,
      display: 'flex',
      flexDirection: 'column'
    }),
    role: "log",
    "aria-label": "Conversation with Livy",
    "aria-busy": streaming
  }, empty && /*#__PURE__*/React.createElement(Stack, {
    alignItems: "center",
    justifyContent: "flex-end",
    sx: _objectSpread({
      flex: 1,
      pb: '18px',
      textAlign: 'center'
    }, FADE_IN)
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: {
        xs: 26,
        sm: 34
      },
      fontWeight: 700,
      letterSpacing: '-.02em',
      lineHeight: 1.2
    }
  }, "What can I look into?"), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      mt: '10px',
      maxWidth: 520,
      fontSize: 15.5,
      lineHeight: 1.5,
      color: 'text.secondary'
    }
  }, subtitle !== null && subtitle !== void 0 ? subtitle : SUBTITLE[reach])), messages.map((m, i) => /*#__PURE__*/React.createElement(LivyMessage, {
    key: i,
    message: m,
    startsExchange: m.role === 'user' && i > 0,
    conversationId: conversationId
    // Index is a stable id within a conversation: turns are only ever appended.
    ,
    turnId: "t".concat(i),
    surface: surface
    // Only the last message can be mid-stream, and only it should show a caret.
    ,
    streaming: streaming && i === messages.length - 1 && m.role === 'assistant',
    onRetry: !readOnly && !streaming && i === messages.length - 1 && m.role === 'assistant' ? onRetry : undefined,
    onSuggestion: readOnly ? undefined : onSend
  })), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    variant: "outlined",
    sx: {
      mt: '8px',
      borderRadius: '10px'
    },
    action: onRetry && !readOnly ? /*#__PURE__*/React.createElement(Button, {
      size: "small",
      color: "inherit",
      onClick: onRetry,
      sx: {
        textTransform: 'none'
      }
    }, "Try again") : undefined
  }, error), /*#__PURE__*/React.createElement("div", {
    ref: endRef
  }))), !atBottom && messages.length > 0 && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    onClick: () => {
      var _endRef$current2;
      return (_endRef$current2 = endRef.current) === null || _endRef$current2 === void 0 ? void 0 : _endRef$current2.scrollIntoView({
        block: 'end',
        behavior: 'smooth'
      });
    },
    sx: {
      position: 'absolute',
      bottom: 12,
      left: '50%',
      transform: 'translateX(-50%)',
      borderRadius: '14px',
      textTransform: 'none',
      fontSize: 12,
      px: '12px',
      py: '3px',
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
      '&:hover': {
        bgcolor: 'action.hover'
      }
    }
  }, "\u2193 Latest")), !readOnly && /*#__PURE__*/React.createElement(Box, {
    sx: {
      px: {
        xs: '16px',
        sm: '24px'
      }
    }
  }, /*#__PURE__*/React.createElement(LivyLiveBanner, {
    live: live
  })), !readOnly && /*#__PURE__*/React.createElement(LivyComposer, {
    onSend: onSend,
    onStop: onStop,
    streaming: streaming,
    placeholder: (live === null || live === void 0 ? void 0 : live.state) === 'joined' ? 'Message Livable support' : 'Ask Livy anything',
    disclaimer: empty ? undefined : DISCLAIMER[reach],
    actions: actions,
    active: active
  }), /*#__PURE__*/React.createElement(Box, {
    "aria-hidden": !empty,
    sx: {
      flex: empty ? '1 1 0' : '0 0 0',
      minHeight: 0,
      overflowY: empty ? 'auto' : 'hidden',
      opacity: empty ? 1 : 0,
      px: {
        xs: '16px',
        sm: '24px'
      },
      transition: 'flex-grow .5s cubic-bezier(.2,.8,.2,1), opacity .25s ease',
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none'
      }
    }
  }, empty && /*#__PURE__*/React.createElement(Box, {
    sx: _objectSpread(_objectSpread({}, COLUMN), {}, {
      pt: '8px',
      pb: '24px'
    })
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '10px'
    }
  }, starters.map((s, i) =>
  /*#__PURE__*/
  // ButtonBase, not Button: the app's Button theme adds weight and a card shadow.
  React.createElement(ButtonBase, {
    key: s,
    onClick: () => onSend(s),
    sx: _objectSpread(_objectSpread({}, SUGGESTION_CHIP), {}, {
      animationDelay: "".concat(120 + i * 60, "ms")
    })
  }, /*#__PURE__*/React.createElement(StarterIcon, {
    text: s
  }), s))), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    sx: {
      display: 'block',
      textAlign: 'center',
      mt: '22px',
      fontSize: 11,
      opacity: 0.7
    }
  }, DISCLAIMER[reach]))));
}

/** Ripples off inside Livy: see LivyTheme. */
export function LivyPanel(props) {
  var _props$live;
  return /*#__PURE__*/React.createElement(LivyTheme, null, /*#__PURE__*/React.createElement(LivyLiveContext.Provider, {
    value: {
      live: (_props$live = props.live) !== null && _props$live !== void 0 ? _props$live : null,
      onLive: props.onLive
    }
  }, /*#__PURE__*/React.createElement(LivyPanelBody, props)));
}